import { useCallback, useMemo } from 'react'
import { useTimeZone } from '@/providers/TimeZoneProvider'
import { formatDateTimeLong } from '@/utils/timezone'
import type { ShiftItem } from '@/types/api/shifts'
import { useShift } from '@/hooks/api/useShifts'
import { useGetAllDispatchers } from '@/hooks/api/useDispatcher'
import { Clock, X } from 'lucide-react'

interface ClockInReminderModalProps {
  isOpen: boolean
  shift: ShiftItem | null
  onClose: () => void
  onStartClockIn: () => void
}

const formatShiftType = (shiftType?: string | null) => {
  if (!shiftType) return 'Shift'
  return `${shiftType.charAt(0).toUpperCase()}${shiftType.slice(1)} Shift`
}

const formatAssignment = (assignment?: string | null) => {
  if (!assignment) return '-'
  return `${assignment.charAt(0).toUpperCase()}${assignment.slice(1)}`
}

const getDispatcherName = (
  dispatcher?: {
    first_name?: string | null
    last_name?: string | null
  } | null
) => {
  const name = [dispatcher?.first_name, dispatcher?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()

  return name || '-'
}

const getBackupDispatcherName = (shift: ShiftItem) => {
  const backupDetails = shift.backup_dispatcher_details as
    | {
        first_name?: string | null
        last_name?: string | null
      }
    | null
    | undefined

  const backupName = getDispatcherName(backupDetails)
  if (backupName !== '-') return backupName

  return shift.backup_dispatcher || '-'
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="border-t border-[#DADDE1] py-2.5 text-sm">
    <span className="text-[#292D32]">{label}: </span>
    <span className="ml-2 font-semibold text-[#1B1D20]">{value}</span>
  </div>
)

const TimeRange = ({
  from,
  to,
  timeZone,
  compact = false,
}: {
  from: string
  to: string
  timeZone: string
  compact?: boolean
}) => (
  <div className="flex gap-3 text-sm text-[#1B1D20]">
    <Clock size={16} className="mt-0.5 shrink-0 text-[#6B7787]" />
    <div className="space-y-1">
      <div className="grid grid-cols-[42px_1fr] gap-3">
        <span className={compact ? 'font-medium' : ''}>From</span>
        <span className={compact ? 'font-medium' : ''}>
          {formatDateTimeLong(from, timeZone, '-')}
        </span>
      </div>
      <div className="grid grid-cols-[42px_1fr] gap-3">
        <span className={compact ? 'font-medium' : ''}>To</span>
        <span className={compact ? 'font-medium' : ''}>
          {formatDateTimeLong(to, timeZone, '-')}
        </span>
      </div>
    </div>
  </div>
)

const ClockInReminderModal: React.FC<ClockInReminderModalProps> = ({
  isOpen,
  shift,
  onClose,
  onStartClockIn,
}) => {
  const { timeZone } = useTimeZone()
  const { data: dispatchersData, isLoading: isLoadingDispatchers } =
    useGetAllDispatchers()
  const dispatcherMap = useMemo(() => {
    const dispatchers = dispatchersData?.data?.dispatchers ?? []

    return new Map<
      string,
      {
        first_name?: string | null
        last_name?: string | null
      }
    >(
      dispatchers.map((dispatcher: any) => [
        dispatcher.dispatcher_id,
        dispatcher,
      ])
    )
  }, [dispatchersData?.data?.dispatchers])

  const getDispatcherNameById = useCallback(
    (dispatcherId?: string | null) => {
      if (!dispatcherId) return '-'

      const dispatcher = dispatcherMap.get(dispatcherId)
      if (dispatcher) return getDispatcherName(dispatcher)

      return isLoadingDispatchers
        ? 'Loading dispatcher...'
        : 'Unknown Dispatcher'
    },
    [dispatcherMap, isLoadingDispatchers]
  )

  const {
    data: outgoingShift,
    isLoading: isLoadingOutgoingShift,
    isError: isOutgoingShiftError,
  } = useShift(shift?.shift_id || undefined, {
    enabled: Boolean(isOpen && shift?.shift_id),
    shiftTrail: 'outgoing',
  })

  if (!isOpen || !shift) return null

  const group = shift.roster?.group || shift.roster?.location || '-'
  const backupDispatcher = getBackupDispatcherName(shift)
  const noticePeriod = shift.notice_period || '-'

  const shouldShowUnavailableOutgoingShift =
    !isLoadingOutgoingShift && (isOutgoingShiftError || !outgoingShift)
  const outgoingDispatcherName = outgoingShift?.dispatcher
    ? getDispatcherName(outgoingShift.dispatcher)
    : getDispatcherNameById(outgoingShift?.dispatcher_id)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-2xl rounded-lg bg-white shadow-2xl font-family-sans"
      >
        <div className="flex items-center justify-between border-b border-[#DADDE1] px-4 py-3">
          <div className="text-base font-semibold text-[#64676A]">
            Shift Active
          </div>
          <button onClick={onClose} aria-label="Close clock-in reminder">
            <X
              size={18}
              className="-mt-0.5 text-[#111827] hover:text-gray-600 transition-colors cursor-pointer"
            />
          </button>
        </div>

        <div className="px-4 py-4 text-sm text-[#111827]">
          <div className="mb-4">
            Your scheduled shift is currently active. Please complete the
            clock-in routine to start your shift.
          </div>

          <div className="mb-4 rounded border border-[#6B8EB6] bg-[#E8F1FF] px-3 py-3">
            <div className="mb-3 text-sm font-semibold text-[#23486A]">
              Outgoing Shift
            </div>

            {isLoadingOutgoingShift ? (
              <p className="text-sm font-medium text-[#5D6673]">
                Loading outgoing shift details...
              </p>
            ) : shouldShowUnavailableOutgoingShift ? (
              <p className="text-sm font-medium text-[#5D6673]">
                Outgoing shift details unavailable.
              </p>
            ) : outgoingShift ? (
              <div className="space-y-3">
                <div className="text-sm">
                  <span>Dispatcher: </span>
                  <span className="ml-2 font-semibold">
                    {outgoingDispatcherName}
                  </span>
                </div>
                <div className="border-t border-dashed border-[#23486A]" />
                <div className="text-sm">
                  <span>Shift: </span>
                  <span className="font-semibold">
                    {formatShiftType(outgoingShift.shift_type)}
                  </span>
                </div>
                <TimeRange
                  from={outgoingShift.from}
                  to={outgoingShift.to}
                  timeZone={timeZone}
                  compact
                />
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold text-[#23486A]">
              Incoming Shift
            </div>
            <div className="mb-3 text-sm">
              <span>Shift: </span>
              <span className="font-semibold">
                {formatShiftType(shift.shift_type)}
              </span>
            </div>
            <div className="mb-3">
              <TimeRange
                from={shift.from}
                to={shift.to}
                timeZone={timeZone}
                compact
              />
            </div>

            <DetailRow label="Group" value={group} />
            <DetailRow label="Back-up Dispatcher" value={backupDispatcher} />
            <DetailRow label="Notice Period" value={noticePeriod} />
            <DetailRow
              label="Assignment"
              value={formatAssignment(shift.role)}
            />
          </div>
        </div>

        <div className="flex justify-end px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={onStartClockIn}
            className="rounded-md bg-[#23486A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1d3b57] cursor-pointer"
          >
            Start Clock-In Routine
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClockInReminderModal
